import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Avatar from "@/components/Avatar";
import { Loader2, Trophy, Medal, Shield, DollarSign } from "lucide-react";
import RecapBanner from "@/components/RecapBanner";

const ordinal = (n) => {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};
const money = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function PotPanel({ data }) {
  const pot = data.pot_total || 0;
  const payouts = data.payouts || [];
  if (pot <= 0 && payouts.length === 0) return null;
  const leaders = data.rows || [];
  const paidCount = payouts.length;
  const cashRanks = new Set();
  for (let rank = 1; rank <= paidCount; rank++) {
    for (const r of leaders) if (r.rank === rank) cashRanks.add(r.user_id);
  }

  return (
    <div className="max-w-7xl mx-auto mb-6 grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-3" data-testid="pot-panel">
      <div className="relative overflow-hidden border-2 border-[#EAB308]/50 bg-[#12100a] p-5">
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(circle at 100% 0%, rgba(234,179,8,0.18), transparent 55%)" }} />
        <div className="relative">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="w-4 h-4 text-[#EAB308]" />
            <span className="text-[10px] uppercase tracking-[0.3em] text-[#EAB308]">Season Pot</span>
          </div>
          <div className="font-['Bebas_Neue'] text-5xl tracking-wide leading-none text-white tabular-nums" data-testid="pot-total">
            {money(pot)}
          </div>
          <div className="text-xs text-white/50 mt-2">
            {paidCount > 0 ? `${paidCount} paid position${paidCount === 1 ? "" : "s"}` : "Payout structure not set"}
          </div>
        </div>
      </div>

      <div className="border border-white/10 bg-[#141414]">
        <div className="px-4 py-2.5 border-b border-white/10 flex items-center justify-between">
          <span className="text-[11px] uppercase tracking-[0.3em] text-white/60">Payout Structure</span>
          <span className="text-[10px] uppercase tracking-widest text-white/40">Current standings</span>
        </div>
        {payouts.length === 0 ? (
          <div className="px-4 py-6 text-center text-white/40 text-sm">No payout positions configured yet.</div>
        ) : (
          <div className="divide-y divide-white/5">
            {payouts.map((pct, i) => {
              const amount = pot * ((parseFloat(pct) || 0) / 100);
              const leader = leaders.find((r) => r.rank === i + 1);
              return (
                <div key={i} className="grid grid-cols-[60px_1fr_90px_1fr] gap-3 items-center px-4 py-2.5" data-testid={`payout-line-${i}`}>
                  <span className="font-['Bebas_Neue'] text-2xl tracking-wide leading-none text-[#EAB308]">{ordinal(i + 1)}</span>
                  <span className="text-sm text-white/70 tabular-nums">{(parseFloat(pct) || 0).toFixed(2)}%</span>
                  <span className="font-['Bebas_Neue'] text-xl tracking-wide leading-none text-white tabular-nums" data-testid={`payout-line-amount-${i}`}>
                    {money(amount)}
                  </span>
                  <span className="text-sm text-white/60 truncate">
                    {leader ? (
                      <span className="flex items-center gap-2">
                        <span className="text-white">{leader.username}</span>
                        <span className="text-[10px] uppercase tracking-widest text-white/40">{leader.season_total} PTS</span>
                      </span>
                    ) : (
                      <span className="text-white/25">—</span>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Standings() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [weeks, setWeeks] = useState([]);
  const [recapWeek, setRecapWeek] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get("/standings"), api.get("/weeks")])
      .then(([s, w]) => {
        setData(s.data);
        setWeeks(w.data);
        const decided = [...w.data].reverse().find((x) => x.decided > 0);
        if (decided) setRecapWeek(decided.week_number);
      })
      .finally(() => setLoading(false));
  }, []);

  const seasonComplete = weeks.length > 0 && weeks.every((w) => w.games_count > 0 && w.decided === w.games_count);
  const champions = data ? data.rows.filter((r) => r.rank === 1) : [];

  const showCovers = data && (data.pick_mode === "combo" || data.top_covers > 0);
  const isCoverKing = (r) => showCovers && data.cover_kings.includes(r.username);

  const rankColor = (r) =>
    r === 1 ? "text-[#EAB308]" : r === 2 ? "text-white/70" : r === 3 ? "text-[#CD7F32]" : "text-white/40";

  return (
    <div className="max-w-full mx-auto px-4 py-8">
      <div className="max-w-7xl mx-auto mb-6">
        <h1 className="font-['Bebas_Neue'] text-5xl tracking-tight leading-none">SEASON STANDINGS</h1>
        <p className="text-white/50 text-sm mt-1">18 weeks. Every point counts toward the season crown.</p>
      </div>

      {loading || !data ? (
        <div className="flex items-center gap-2 text-white/50 py-20 justify-center">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading standings…
        </div>
      ) : (
        <>
          {seasonComplete && champions.length > 0 && (
            <div className="max-w-7xl mx-auto mb-8" data-testid="champion-banner">
              <div className="relative overflow-hidden border-2 border-[#EAB308]/60 bg-[#12100a] px-6 py-10 text-center animate-[fadeIn_0.6s_ease]">
                <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(circle at 50% -10%, rgba(234,179,8,0.22), transparent 60%)" }} />
                <div className="relative">
                  <Trophy className="w-12 h-12 text-[#EAB308] mx-auto mb-3" />
                  <div className="text-[11px] uppercase tracking-[0.35em] text-[#EAB308] mb-3">
                    {champions.length > 1 ? "2026 Season Co-Champions" : "2026 Season Champion"}
                  </div>
                  {champions.map((c) => (
                    <div key={c.user_id} className="flex items-center justify-center gap-3 mb-1" data-testid={`champion-${c.username}`}>
                      <Avatar team={c.avatar} username={c.username} size="lg" />
                      <span className="font-['Bebas_Neue'] text-5xl sm:text-7xl tracking-tight leading-none text-white">{c.username}</span>
                    </div>
                  ))}
                  <div className="font-['Bebas_Neue'] text-3xl tracking-wide text-[#E31837] mt-3">{champions[0].season_total} PTS</div>
                  {champions.length > 1 && <div className="text-xs text-white/50 mt-2">Season ended in a tie for first.</div>}
                </div>
              </div>
            </div>
          )}
          {recapWeek && (
            <div className="max-w-7xl mx-auto">
              <RecapBanner weekNumber={recapWeek} />
            </div>
          )}
          <PotPanel data={data} />
          {showCovers && data.top_covers > 0 && (
            <div className="max-w-7xl mx-auto mb-6 flex flex-wrap items-center gap-3 bg-[#141414] border border-[#EAB308]/30 px-5 py-3" data-testid="cover-leaderboard">
              <Shield className="w-5 h-5 text-[#EAB308]" />
              <span className="text-[10px] uppercase tracking-[0.25em] text-[#EAB308]">Cover {data.cover_kings.length > 1 ? "Kings" : "King"}</span>
              <span className="font-['Bebas_Neue'] text-2xl tracking-wide leading-none">{data.cover_kings.join(" · ")}</span>
              <span className="text-xs text-white/50">{data.top_covers} spread pick{data.top_covers === 1 ? "" : "s"} covered this season</span>
            </div>
          )}
          {/* Top 5 */}
          <div className="max-w-7xl mx-auto grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-8">
            {data.rows.slice(0, 5).map((r, i) => (
              <div
                key={r.user_id}
                className={`bg-[#141414] border p-4 flex flex-col gap-2 ${
                  i === 0 ? "border-[#EAB308]/50" : "border-white/10"
                }`}
                data-testid={`podium-${i + 1}`}
              >
                <div className="flex items-center justify-between">
                  {i === 0 ? <Trophy className="w-6 h-6 text-[#EAB308]" /> : <Medal className={`w-6 h-6 ${rankColor(i + 1)}`} />}
                  <span className="text-[10px] uppercase tracking-[0.2em] text-white/40">#{r.rank}</span>
                </div>
                <div className="font-['Bebas_Neue'] text-xl tracking-wide leading-none truncate">{r.username}</div>
                <div className="font-['Bebas_Neue'] text-3xl tracking-wide text-[#E31837] leading-none">{r.season_total}</div>
              </div>
            ))}
          </div>

          {/* Full matrix */}
          <div className="max-w-7xl mx-auto border border-white/10 overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-[#0A0A0A]">
                  <th className="sticky left-0 z-10 bg-[#0A0A0A] px-3 py-3 text-left text-[11px] uppercase tracking-[0.2em] text-white/50 border-b border-white/10">#</th>
                  <th className="sticky left-8 z-10 bg-[#0A0A0A] px-3 py-3 text-left text-[11px] uppercase tracking-[0.2em] text-white/50 border-b border-white/10 min-w-[130px]">Player</th>
                  {data.week_numbers.map((wn) => (
                    <th key={wn} className="px-2 py-3 text-center text-[11px] uppercase tracking-[0.15em] text-white/50 border-b border-white/10 border-l border-white/5 min-w-[52px]">
                      W{wn}
                    </th>
                  ))}
                  {showCovers && (
                    <th className="px-2 py-3 text-center text-[11px] uppercase tracking-[0.15em] text-[#EAB308] border-b border-white/10 border-l border-white/10" title="Spread picks covered">CV</th>
                  )}
                  <th className="px-4 py-3 text-right text-[11px] uppercase tracking-[0.2em] text-white/80 border-b border-white/10 border-l border-white/10 bg-[#141414]">Total</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => {
                  const isMe = user && r.username === user.username;
                  return (
                    <tr
                      key={r.user_id}
                      className={`transition-colors duration-150 hover:bg-white/5 ${isMe ? "bg-[#E31837]/10" : ""}`}
                      data-testid={`standings-row-${r.username}`}
                    >
                      <td className={`sticky left-0 z-10 px-3 py-2.5 border-b border-white/10 font-['Bebas_Neue'] text-xl ${rankColor(r.rank)} ${isMe ? "bg-[#1a0b0e]" : "bg-[#0A0A0A]"}`}>
                        {r.rank}
                      </td>
                      <td className={`sticky left-8 z-10 px-3 py-2.5 border-b border-white/10 font-medium whitespace-nowrap ${isMe ? "bg-[#1a0b0e]" : "bg-[#0A0A0A]"}`}>
                        <span className="flex items-center gap-2">
                          <Avatar team={r.avatar} username={r.username} size="sm" />
                          <Link to={`/profile/${r.username}`} className="hover:text-[#E31837] transition-colors duration-200" data-testid={`profile-link-${r.username}`}>
                            {r.username}
                          </Link>
                          {isMe && <span className="text-[10px] text-[#E31837] uppercase">You</span>}
                        </span>
                      </td>
                      {data.week_numbers.map((wn) => {
                        const cell = r.weekly[String(wn)];
                        const show = cell && cell.decided > 0;
                        return (
                          <td key={wn} className="px-2 py-2.5 text-center border-b border-white/10 border-l border-white/5 tabular-nums">
                            {show ? (
                              <span className={cell.total > 0 ? "text-white" : cell.total < 0 ? "text-[#EF4444]" : "text-white/40"}>
                                {cell.total}
                              </span>
                            ) : (
                              <span className="text-white/15">–</span>
                            )}
                          </td>
                        );
                      })}
                      {showCovers && (
                        <td className="px-2 py-2.5 text-center border-b border-white/10 border-l border-white/10 tabular-nums" data-testid={`covers-${r.username}`}>
                          <span className={`inline-flex items-center gap-1 ${r.covers > 0 ? "text-[#EAB308]" : "text-white/20"}`}>
                            {isCoverKing(r) && <Shield className="w-3 h-3" />}
                            {r.covers > 0 ? r.covers : "–"}
                          </span>
                        </td>
                      )}
                      <td className="px-4 py-2.5 text-right border-b border-white/10 border-l border-white/10 bg-[#141414]">
                        <span className="font-['Bebas_Neue'] text-2xl tracking-wide text-[#E31837]">{r.season_total}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
